import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import axios from 'axios';
import { Search, Plus, Trash2, List, Megaphone, MessageSquare, HelpCircle, Users2 } from 'lucide-react';
import Pagination from '../../components/common/Pagination';
import BoardTable from '../../components/board/BoardTable';

function ListPage({ boardType = 'Notice' }) {
  const { me, menuItems } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();

  const [adminBtn, setAdminBtn] = useState(false);
  const [adminCol, setAdminCol] = useState(true);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [checkedIds, setCheckedIds] = useState([]);
  const [pageTitle, setPageTitle] = useState('게시판');
  const limit = 15;

  const BOARD_TABS = [
    { type: 'Notice', label: '공지사항', icon: Megaphone },
    { type: 'Free', label: '자유게시판', icon: MessageSquare },
    { type: 'CoWork', label: '협업게시판', icon: Users2 },
    { type: 'QnA', label: '질문게시판', icon: HelpCircle },
  ];

  useEffect(() => {
    const targetUrl = `/${boardType}/List`;
    for (const parent of menuItems || []) {
      const match = parent.items?.find((child) => child.url === targetUrl);
      if (match) {
        setPageTitle(match.name);
        break;
      }
    }
  }, [menuItems, boardType]);

  useEffect(() => {
    const fetchArticles = async () => {
      let url = '/board';
      const params = { page: currentPage, limit, boardType };
      if (selectedCategory) {
        url = '/board/category';
        params.query = selectedCategory;
      } else if (searchQuery) {
        url = '/board/search';
        params.query = searchQuery;
      }
      try {
        const res = await axios.get(url, { params, withCredentials: true });
        const serverArticles = res.data.data.map((article) => ({
          ...article,
          isChecked: false,
        }));
        setArticles(serverArticles);
        setTotalArticles(res.data.total);
        setTotalPages(res.data.totalPages);
      } catch (err) {
        console.error('게시글 로딩 실패:', err);
      }
    };
    fetchArticles();
  }, [currentPage, selectedCategory, searchQuery, boardType]);

  useEffect(() => {
    axios
      .get('/board/category/load', {
        params: { boardType },
        withCredentials: true,
      })
      .then((res) => setCategories(res.data))
      .catch(console.error);
  }, [boardType]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchText);
    setSelectedCategory('');
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSearchText('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleReset = () => {
    setSearchText('');
    setSearchQuery('');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  const deleteArticles = () => {
    if (!checkedIds.length) return;
    if (!window.confirm(`선택한 ${checkedIds.length}개의 게시글을 삭제하시겠습니까?`)) return;
    axios.post('/board/select/delete/', { checkedIds, boardType }).then(() => {
      setArticles((prev) => prev.filter((a) => !checkedIds.includes(a.id)));
      setCheckedIds([]);
      setAdminCol(true);
      setAdminBtn(false);
    });
  };

  const writeArticle = () => {
    navigate(`/${boardType}/Write?boardType=${boardType}`);
  };

  const canWrite = () => {
    if (!me) return false;
    if (boardType === 'Notice') return me.adminYn === 'Y';
    return true;
  };

  const isFiltered = searchQuery || selectedCategory;

  return (
    <>
      {/* 페이지 헤더 */}
      <div className="flex mb-8 justify-center items-center">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center">
          커뮤니티
        </h1>
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-2 mb-4 flex-wrap">
          {BOARD_TABS.map(({ type, label, icon: Icon }) => (
            <Link
              key={type}
              to={`/${type}/List`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                boardType === type
                  ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                  : 'bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]'
              }`}>
              <Icon size={14} />
              {label}
            </Link>
          ))}
      </div>

      {/* 툴바: 글 수 + 카테고리 + 검색 */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400 whitespace-nowrap">
            총 <span className="text-sky-400 font-semibold">{totalArticles}</span>건
          </span>
          <select
            className="px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
            onChange={handleCategoryChange}
            value={selectedCategory}>
            <option value="">전체 카테고리</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.category}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 sm:max-w-sm sm:ml-auto">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="text"
              placeholder="검색어를 입력하세요"
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
            />
          </div>
          <button
            onClick={handleSearchSubmit}
            className="shrink-0 px-4 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-slate-400 text-sm font-medium hover:bg-white/5 hover:text-white transition-colors">
            검색
          </button>
        </div>
      </div>

      {/* 필터 상태 표시 */}
      {isFiltered && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-slate-500">
            {searchQuery && `"${searchQuery}" 검색 결과`}
            {selectedCategory && categories.find(c => c.id.toString() === selectedCategory)?.category && `"${categories.find(c => c.id.toString() === selectedCategory).category}" 필터`}
          </span>
          <button
            onClick={handleReset}
            className="text-xs text-sky-400 hover:text-sky-300 transition-colors">
            초기화
          </button>
        </div>
      )}

      {/* 게시글 테이블 */}
      <BoardTable
        articles={articles}
        totalArticles={totalArticles}
        currentPage={currentPage}
        limit={limit}
        me={me}
        boardType={boardType}
        adminCol={adminCol}
        setCheckedIds={setCheckedIds}
      />

      {/* 페이지네이션 */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      {/* 하단 버튼 */}
      <div className="flex items-center justify-end mt-5 gap-2">
        {isFiltered && (
          <button
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-600 hover:bg-slate-500 text-white text-sm font-semibold transition-colors"
            onClick={handleReset}>
            <List size={14} />
            전체목록
          </button>
        )}
        {adminBtn && checkedIds.length > 0 && (
          <button
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold transition-colors"
            onClick={deleteArticles}>
            <Trash2 size={14} />
            삭제 ({checkedIds.length})
          </button>
        )}
        {canWrite() && (
          <button
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-colors"
            onClick={writeArticle}>
            <Plus size={14} />
            글쓰기
          </button>
        )}
      </div>
    </>
  );
}

export default ListPage;
